import { isSharedPlayback, sharedLibraryRows } from '../../api/libraryPlayback';
import ApprovedStoryActions from '../../components/ApprovedStoryActions/ApprovedStoryActions';
import SharedStoryActions from '../../components/ApprovedStoryActions/SharedStoryActions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Story, useApp } from '../../context/AppContext';
import { BillingView, fetchBilling } from '../../api/billingApi';
import { RenderListItem, enqueueRender, fetchRenders, retryRender } from '../../api/rendersApi';
import { sharedReviewEnabled } from '../../audio/personalMessagesApi';
import { Catalog, sharedCatalog } from '../../api/sharedHouseholdApi';
import { isAbortError, toApiError } from '../../api/http';
import StatePanel from '../../components/StatePanel/StatePanel';
import { useBedtimeContext } from '../../context/BedtimeContext';
import StoryWorlds from '../../components/StoryWorlds/StoryWorlds';
import StorySearch from '../../components/StoryWorlds/StorySearch';
import StoryWorldTile from '../../components/StoryWorlds/StoryWorldTile';
import { groupStoryWorlds, StoryWorld } from '../../components/StoryWorlds/storyWorldModel';
import BedtimePairChip from '../../components/BedtimeContext/BedtimePairChip';
import { EmptyState, PillButton, Sheet, Shelf, StateMessage } from '../../components/ui';
import { SearchIcon } from '../../components/icons';
import { formatDurationLabel } from '../../utils/duration';
import { familyVoicePhrase } from '../../copy/terminology';
import { buildDiscoveryRails, chooseTonightStory } from './discoveryModel';
import './Discover.css';

const RENDER_PREFERENCE: RenderListItem['status'][] = ['parent_approved', 'machine_passed', 'needs_review', 'regenerating', 'rendering', 'queued', 'failed'];

interface RailTile {
  world: StoryWorld;
  to: string;
  duration: string | null;
  metadata?: string[];
}

const Discover: React.FC = () => {
  const { state, fetchStories } = useApp();
  const context = useBedtimeContext();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requestedStory = params.get('story');
  const [searchOpen, setSearchOpen] = useState(false);
  const [fundingChoice, setFundingChoice] = useState<{ storyId: string; billing: BillingView } | null>(null);
  const [isGenerating, setIsGenerating] = useState<string | null>(null);
  const [shared, setShared] = useState<{key: string; catalog: Catalog} | null>(null);
  const [renderByStory, setRenderByStory] = useState<Record<string, RenderListItem>>({});
  const [renderState, setRenderState] = useState<'loading' | 'success' | 'empty' | 'error'>('loading');
  const [renderError, setRenderError] = useState<ReturnType<typeof toApiError> | null>(null);
  const [generationError, setGenerationError] = useState<{ storyId: string; error: ReturnType<typeof toApiError> } | null>(null);
  const [renderRetryNonce, setRenderRetryNonce] = useState(0);
  const [renderPairKey, setRenderPairKey] = useState<string | null>(null);
  const generationControllerRef = useRef<AbortController | null>(null);
  const retryRequestRef = useRef(0);
  const renderRequestRef = useRef(0);
  const pair = context.pair;
  const currentPairKey = pair ? `${pair.householdId}:${pair.listenerId}:${pair.voiceId}` : '';
  const pairKeyRef = useRef(currentPairKey);
  pairKeyRef.current = currentPairKey;

  useEffect(() => { void fetchStories(); }, [fetchStories]);

  useEffect(() => {
    if (!pair || context.status === 'loading' || context.status === 'needs_selection' || context.status === 'error' || context.status === 'no_ready_voice' || context.status === 'no_listener' || context.status === 'missing_bookends') {
      setRenderByStory({});
      setRenderPairKey(null);
      setIsGenerating(null);
      generationControllerRef.current?.abort();
      retryRequestRef.current += 1;
      renderRequestRef.current += 1;
      return undefined;
    }
    const controller = new AbortController();
    const requestId = ++renderRequestRef.current;
    const pairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    setFundingChoice(null);
    setRenderState('loading');
    setRenderError(null);
    setGenerationError(null);
    setRenderPairKey(null);
    setShared(null);
    Promise.all([
      fetchRenders({ householdId: pair.householdId, listenerId: pair.listenerId, voiceId: pair.voiceId, signal: controller.signal }),
      sharedReviewEnabled ? sharedCatalog(pair, controller.signal) : Promise.resolve(null),
    ]).then(([renders, catalog]) => {
        if (requestId !== renderRequestRef.current || controller.signal.aborted || pairKey !== pairKeyRef.current) return;
        setShared(catalog ? {key: pairKey, catalog} : null);
        const byStory: Record<string, RenderListItem> = {};
        renders.filter((render) => render.household_id === pair.householdId && render.listener_id === pair.listenerId && render.voice_id === pair.voiceId && RENDER_PREFERENCE.includes(render.status)).forEach((render) => {
          const existing = byStory[render.story_id];
          if (!existing || RENDER_PREFERENCE.indexOf(render.status) < RENDER_PREFERENCE.indexOf(existing.status) || (render.status === existing.status && render.created_at > existing.created_at)) byStory[render.story_id] = render;
        });
        if (catalog) sharedLibraryRows(catalog, pair).forEach(row => { byStory[row.story_id] = row; });
        setRenderByStory(byStory);
        setRenderPairKey(pairKey);
        setRenderState(Object.keys(byStory).length ? 'success' : 'empty');
      })
      .catch((error) => {
        if (!isAbortError(error) && !controller.signal.aborted && requestId === renderRequestRef.current && pairKey === pairKeyRef.current) {
          setRenderError(toApiError(error)); setRenderState('error');
        }
      });
    return () => {
      controller.abort();
      renderRequestRef.current += 1;
      generationControllerRef.current?.abort();
      retryRequestRef.current += 1;
      setIsGenerating(null);
    };
  }, [context.status, pair, renderRetryNonce]);

  const activeShared = shared?.key === currentPairKey ? shared.catalog : null;
  const filteredStories = state.stories;
  const listener = context.listeners.find((item) => item.listener_id === pair?.listenerId);
  const voice = context.voices.find((item) => item.voice_id === pair?.voiceId);
  const listenerName = listener?.display_name || 'your family';
  const storytellerName = familyVoicePhrase(voice?.voice_name);
  const visibleRenderByStory = useMemo<Record<string, RenderListItem>>(() => (pair && renderPairKey === currentPairKey ? renderByStory : {}), [pair, renderPairKey, currentPairKey, renderByStory]);
  const featuredStory = chooseTonightStory(filteredStories, visibleRenderByStory);
  const orderedStories = featuredStory ? [featuredStory, ...filteredStories.filter((story) => story.story_id !== featuredStory.story_id)] : filteredStories;
  const storiesPending = state.isLoading && state.stories.length === 0;
  const storiesFailed = Boolean(state.error && state.stories.length === 0);
  const canShowCatalog = context.status === 'ready' && !storiesPending && !storiesFailed;

  // Shelves: one representative story per world so a book is one tile, then
  // the existing rails decide which shelf each world belongs to.
  const rails = useMemo(() => {
    const worlds = groupStoryWorlds(filteredStories);
    const worldByRepresentative = new Map<string, StoryWorld>();
    const representativeRenders: Record<string, RenderListItem> = {};
    const approvedChapter = new Map<string, Story>();
    const representatives = worlds.map((world) => {
      const representative = world.chapters[0];
      worldByRepresentative.set(representative.story_id, world);
      const approved = world.chapters.find((chapter) => visibleRenderByStory[chapter.story_id]?.status === 'parent_approved');
      if (approved) {
        representativeRenders[representative.story_id] = visibleRenderByStory[approved.story_id];
        approvedChapter.set(representative.story_id, approved);
      }
      return representative;
    });
    return buildDiscoveryRails(representatives, representativeRenders).map((rail) => ({
      id: rail.id,
      title: rail.title,
      tiles: rail.stories.map((representative): RailTile => {
        const world = worldByRepresentative.get(representative.story_id)!;
        const approved = approvedChapter.get(representative.story_id);
        const target = approved ?? representative;
        const render = approved ? visibleRenderByStory[approved.story_id] : undefined;
        return {
          world,
          to: `/discover?story=${encodeURIComponent(target.story_id)}`,
          duration: formatDurationLabel(target.estimated_duration_minutes, render?.output_seconds),
          metadata: rail.id === 'ready-library' ? [`Ready in ${storytellerName}`] : undefined,
        };
      }),
    }));
  }, [filteredStories, storytellerName, visibleRenderByStory]);

  const handleQuickGenerate = async (storyId: string, subscriptionId?: string) => {
    if (!pair || !voice || context.status === 'missing_bookends') return;
    const requestPairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    generationControllerRef.current?.abort();
    const controller = new AbortController();
    generationControllerRef.current = controller;
    setIsGenerating(storyId);
    setGenerationError(null);
    try {
      if (!subscriptionId) {
        const billing = await fetchBilling(pair.householdId, controller.signal);
        if (controller.signal.aborted || requestPairKey !== pairKeyRef.current) return;
        if (billing.enabled || billing.subscription) { setFundingChoice({ storyId, billing }); return; }
      }
      await enqueueRender(storyId, voice.voice_id, pair.listenerId, controller.signal, undefined, subscriptionId, filteredStories.find(story => story.story_id === storyId)?.content_scope?.content_sha256);
      setFundingChoice(null);
      if (controller.signal.aborted || requestPairKey !== pairKeyRef.current) return;
      navigate('/renders');
    } catch (error) {
      if (!isAbortError(error) && !controller.signal.aborted && requestPairKey === pairKeyRef.current) {
        setGenerationError({ storyId, error: toApiError(error) });
      }
    } finally {
      if (generationControllerRef.current === controller) {
        generationControllerRef.current = null;
        setIsGenerating(null);
      }
    }
  };

  const handleRetryRender = async (render: RenderListItem) => {
    if (!pair || !voice) return;
    const requestPairKey = `${pair.householdId}:${pair.listenerId}:${pair.voiceId}`;
    const requestId = ++retryRequestRef.current;
    setIsGenerating(render.story_id);
    setGenerationError(null);
    try {
      const result = await retryRender(render.render_id);
      if (requestId !== retryRequestRef.current || requestPairKey !== pairKeyRef.current) return;
      setRenderByStory((current) => ({
        ...current,
        [render.story_id]: {
          ...(current[render.story_id] || render),
          render_id: result.render_id || render.render_id,
          status: result.status === 'already_rendering' ? 'rendering' : 'queued',
        },
      }));
      setRenderPairKey(requestPairKey);
      setRenderState('success');
    } catch (error) {
      if (requestId === retryRequestRef.current && requestPairKey === pairKeyRef.current) {
        setGenerationError({ storyId: render.story_id, error: toApiError(error) });
      }
    } finally {
      if (requestId === retryRequestRef.current) setIsGenerating(null);
    }
  };

  const storyAction = (story: Story, compact = false) => {
    const render = visibleRenderByStory[story.story_id];
    if (pair && context.status === 'ready' && (renderState === 'loading' || (renderState !== 'error' && renderPairKey !== currentPairKey))) return <span role="status">Checking recording…</span>;
    if (pair && renderState === 'error') return <div className="discover-story-action"><p>Recording status unavailable</p><button type="button" className="discover-story-link" onClick={() => setRenderRetryNonce((n) => n + 1)}>Try again</button></div>;
    const actionClass = compact ? 'discover-story-link' : 'btn btn-primary';
    const saved = activeShared?.stories.find((item) => item.story_id === story.story_id);
    if (render?.status === 'parent_approved' && !isSharedPlayback(render.render_id)) return <ApprovedStoryActions story={render} title={story.title} />;
    if (saved && pair && activeShared) return <SharedStoryActions key={`${currentPairKey}:${saved.story_id}`} story={saved} pair={pair} messages={activeShared.messages} />;
    if (render?.status === 'parent_approved') return <ApprovedStoryActions story={render} title={story.title} />;
    if (render) {
      if (render.status === 'queued' || render.status === 'rendering' || render.status === 'regenerating') {
        return <span className="discover-story-status" role="status" aria-label="Preparing">Preparing</span>;
      }
      if (render.status === 'failed') {
        const retrying = isGenerating === story.story_id;
        const retryMessage = generationError?.storyId === story.story_id
          ? generationError.error.code === 'offline'
            ? 'Reconnect, then try preparing this story again.'
            : 'The story could not be prepared. Try again in a moment.'
          : null;
        return <div className="discover-story-action"><button type="button" className={actionClass} onClick={() => void handleRetryRender(render)} disabled={retrying} aria-label={`Try again ${story.title} for ${listenerName} with ${storytellerName}`}>{retrying ? 'Preparing…' : 'Try again'}</button>{retryMessage && <StatePanel compact variant="error" title="We couldn’t prepare this story" message={retryMessage} primaryAction={{ label: 'Try again', onClick: () => void handleRetryRender(render) }} secondaryAction={{ label: 'View Tonight', onClick: () => navigate('/renders') }} />}</div>;
      }
      const action = render.status === 'machine_passed'
          ? 'Have a listen'
          : 'View progress';
      return <Link to={`/renders/${render.render_id}`} className={actionClass} aria-label={`${action} ${story.title} for ${listenerName} with ${storytellerName}`}>{action}</Link>;
    }
    if (!pair || !voice) return <button type="button" className={actionClass} onClick={() => context.openPairDialog()}>Choose listener and voice</button>;
    if (context.status === 'missing_bookends') return <Link to={`/voice/${pair.voiceId}/personalize/${pair.listenerId}`} className={compact ? actionClass : 'btn btn-secondary'}>Finish hello &amp; goodnight</Link>;
    const generating = isGenerating === story.story_id;
    const enqueueMessage = generationError?.storyId === story.story_id
      ? generationError.error.code === 'conflict'
        ? 'Add the short personal clips for this listener, then try again.'
        : generationError.error.code === 'offline'
          ? 'Reconnect, then try preparing this story again.'
          : 'Your story was not approved or changed. Try preparing it again.'
      : null;
    return <div className="discover-story-action"><p>{story.content_scope?.label || 'Included content is awaiting review.'}</p><button type="button" className={actionClass} onClick={() => void handleQuickGenerate(story.story_id)} disabled={generating} aria-label={`Prepare ${story.title} for ${listenerName} with ${storytellerName}`}>{generating ? 'Preparing…' : 'Make this story'}</button>{enqueueMessage && <StatePanel compact urgent variant="error" title={generationError?.error.code === 'conflict' ? 'This voice needs a personal goodnight' : 'We couldn’t start this story'} message={enqueueMessage} primaryAction={{ label: 'Try again', onClick: () => void handleQuickGenerate(story.story_id) }} secondaryAction={{ label: 'View Tonight', onClick: () => navigate('/renders') }} />}</div>;
  };

  const openStory = (story: Story) => {
    setSearchOpen(false);
    const next = new URLSearchParams(params);
    next.set('story', story.story_id);
    setParams(next);
  };

  const fundingSheet = (
    <Sheet open={Boolean(fundingChoice)} title="Choose how to make this story" closeLabel="Close" onClose={() => setFundingChoice(null)} describedBy="discover-funding-intro" className="discover-funding">
      <p id="discover-funding-intro" className="discover-funding__intro">A membership unit is used only when you first approve this bedtime. Gift preparation uses its own gift and never also uses membership allowance.</p>
      {fundingChoice?.billing.subscription?.creation_allowed && fundingChoice.billing.subscription.allowance.remaining > 0
        ? <PillButton variant="primary" disabled={Boolean(isGenerating)} busy={Boolean(isGenerating)} busyLabel="Preparing…" onClick={() => void handleQuickGenerate(fundingChoice.storyId, fundingChoice.billing.subscription!.id)}>Use membership</PillButton>
        : <p className="discover-funding__note">Membership creation is currently unavailable. Check your household billing for details.</p>}
      <div className="discover-funding__secondary">
        <PillButton variant="secondary" to="/my-stories">Use a gift</PillButton>
        <PillButton variant="secondary" to={`/account/billing?household=${encodeURIComponent(pair?.householdId || '')}`}>View membership</PillButton>
        <PillButton variant="secondary" onClick={() => setFundingChoice(null)}>Not now</PillButton>
      </div>
    </Sheet>
  );

  const prerequisite = () => {
    switch (context.status) {
      case 'needs_selection':
        return <EmptyState headingLevel={2} title="Choose bedtime" message="Pick who is listening and the Family voice they will hear." action={{ label: 'Choose bedtime', onClick: () => context.openPairDialog() }} />;
      case 'no_listener':
        return <EmptyState headingLevel={2} title="Add a listener first" message="Luma uses a listener to keep stories and goodnights personal." action={{ label: 'Add a listener', to: '/account' }} />;
      case 'no_ready_voice':
        return <EmptyState headingLevel={2} title="Add a family voice" message="Add a family voice before making a story." action={{ label: 'Add a family voice', to: '/voice' }} />;
      case 'missing_bookends':
        return pair ? <EmptyState headingLevel={2} title={`Add ${listenerName}’s hello and goodnight`} message="The Family voice needs three short personal clips before a story can be prepared for this listener." action={{ label: 'Record personal messages', to: `/voice/${pair.voiceId}/personalize/${pair.listenerId}` }} /> : null;
      default:
        return null;
    }
  };

  if (requestedStory && canShowCatalog && filteredStories.length > 0) {
    return (
      <div className="discover discover--immersive luma-immersive">
        <StoryWorlds key={currentPairKey} back={{ to: '/discover', label: 'Back to Discover' }} stories={orderedStories} title="Discover" subtitle={`One story for ${listenerName}, in ${storytellerName}.`} storageKey={`luma:worlds:${state.user?.id || 'anonymous'}:${currentPairKey}`} action={storyAction} duration={(story) => formatDurationLabel(story.estimated_duration_minutes, visibleRenderByStory[story.story_id]?.output_seconds)} status={(story) => visibleRenderByStory[story.story_id]?.status === 'parent_approved' ? <span>{voice?.voice_name || 'Family voice'}</span> : null} />
        {fundingSheet}
      </div>
    );
  }

  return (
    <div className="discover">
      <div className="discover__head">
        <h1>Discover</h1>
        {canShowCatalog && filteredStories.length > 0 && (
          <PillButton variant="secondary" size="compact" icon={<SearchIcon size={20} />} onClick={() => setSearchOpen(true)} aria-haspopup="dialog">
            Search stories
          </PillButton>
        )}
      </div>
      <BedtimePairChip />
      {prerequisite()}
      {storiesPending && <StateMessage kind="loading" title="Loading stories…" shape="shelf" testId="discover-stories-loading" />}
      {storiesFailed && <StateMessage kind="error" title="We couldn’t load stories" message="Try again in a moment. Your listener and voice are still selected." retry={{ onClick: () => void fetchStories() }} testId="discover-stories-error" />}
      {canShowCatalog && renderState === 'error' && <StateMessage kind="error" title="We couldn’t check recordings" error={renderError} retry={{ onClick: () => setRenderRetryNonce((value) => value + 1) }} />}
      {canShowCatalog && filteredStories.length === 0 && <StateMessage kind="empty" title="Your next adventure is on its way" message="Published stories will appear here when they are ready." />}
      {canShowCatalog && rails.map((rail) => (
        <Shelf
          key={rail.id}
          id={`discover-${rail.id}`}
          title={rail.title}
          items={rail.tiles}
          getKey={(tile) => tile.world.id}
          renderItem={(tile, index) => (
            <StoryWorldTile world={tile.world} to={tile.to} duration={tile.duration} metadata={tile.metadata} loading={index < 4 ? 'eager' : 'lazy'} />
          )}
        />
      ))}
      {searchOpen && <StorySearch library={false} stories={filteredStories} onClose={() => setSearchOpen(false)} onSelect={openStory} />}
      {fundingSheet}
    </div>
  );
};

export default Discover;
